import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, MessageSquare, Check, Archive, Trash2 } from 'lucide-react';
import AdminTable from '../../components/admin/AdminTable';
import AdminModal from '../../components/admin/AdminModal';
import { contactAPI } from '../../services/api';

const AdminMessages = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin3256');
      return;
    }

    fetchMessages();
  }, [navigate]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await contactAPI.getAll();
      if (response.records) {
        setMessages(response.records);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (message: any) => {
    try {
      setLoading(true);
      const messageDetails = await contactAPI.getById(message.id);
      setSelectedMessage(messageDetails);
      setShowModal(true);
      
      // If message is new, mark it as read
      if (message.status === 'new') {
        await contactAPI.updateStatus(message.id, 'read');
        setMessages(prev => prev.map(item => 
          item.id === message.id ? { ...item, status: 'read' } : item
        ));
      }
    } catch (error) {
      console.error('Error fetching message details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (message: any) => {
    try {
      if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
        await contactAPI.delete(message.id);
        setMessages(prev => prev.filter(item => item.id !== message.id));
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Erreur lors de la suppression du message');
    }
  };

  const handleUpdateStatus = async (messageId: number, status: string) => {
    try {
      await contactAPI.updateStatus(messageId, status);
      setMessages(prev => prev.map(item => 
        item.id === messageId ? { ...item, status } : item
      ));
      setShowModal(false);
    } catch (error) {
      console.error('Error updating message status:', error);
      alert('Erreur lors de la mise à jour du statut du message');
    }
  };

  const columns = [
    {
      key: 'subject',
      label: 'Sujet',
      sortable: true,
      render: (value: string, row: any) => (
        <div className="flex items-center">
          <div className={`p-2 rounded-lg mr-3 ${
            row.status === 'new' ? 'bg-blue-100 text-blue-600' : 
            row.status === 'read' ? 'bg-gray-100 text-gray-600' :
            row.status === 'replied' ? 'bg-green-100 text-green-600' :
            'bg-purple-100 text-purple-600'
          }`}>
            <MessageSquare className="h-5 w-5" />
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{row.name}</div>
          </div>
        </div>
      )
    },
    {
      key: 'email',
      label: 'Email',
      render: (value: string) => (
        <a href={`mailto:${value}`} className="text-blue-600 hover:underline">
          {value}
        </a>
      )
    },
    {
      key: 'type',
      label: 'Type',
      render: (value: string) => {
        const types: {[key: string]: {label: string, color: string}} = {
          'general': { label: 'Général', color: 'bg-gray-100 text-gray-800' },
          'partnership': { label: 'Partenariat', color: 'bg-blue-100 text-blue-800' },
          'competition': { label: 'Compétition', color: 'bg-green-100 text-green-800' },
          'media': { label: 'Média', color: 'bg-purple-100 text-purple-800' },
          'athlete': { label: 'Athlète', color: 'bg-orange-100 text-orange-800' },
          'federation': { label: 'Fédération', color: 'bg-teal-100 text-teal-800' }
        };
        
        const type = types[value] || { label: value, color: 'bg-gray-100 text-gray-800' };
        
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${type.color}`}>
            {type.label}
          </span>
        );
      }
    },
    {
      key: 'status',
      label: 'Statut',
      render: (value: string) => {
        const statuses: {[key: string]: {label: string, color: string}} = {
          'new': { label: 'Nouveau', color: 'bg-blue-100 text-blue-800' },
          'read': { label: 'Lu', color: 'bg-gray-100 text-gray-800' },
          'replied': { label: 'Répondu', color: 'bg-green-100 text-green-800' },
          'archived': { label: 'Archivé', color: 'bg-purple-100 text-purple-800' }
        };
        
        const status = statuses[value] || { label: value, color: 'bg-gray-100 text-gray-800' };
        
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}>
            {status.label}
          </span>
        );
      }
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <AdminTable
          title="Messages de Contact"
          data={messages}
          columns={columns}
          onView={handleView}
          onDelete={handleDelete}
          searchable
          filterable
          selectable
          pagination
          itemsPerPage={10}
        />

        {/* Message Details Modal */}
        <AdminModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedMessage(null);
          }}
          title="Détails du message"
          size="lg"
        >
          {selectedMessage && (
            <div>
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <div className="flex justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{selectedMessage.subject}</h3>
                    <p className="text-gray-600">
                      De: <span className="font-medium">{selectedMessage.name}</span> ({selectedMessage.email})
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(selectedMessage.created_at).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 mb-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    selectedMessage.type === 'general' ? 'bg-gray-100 text-gray-800' :
                    selectedMessage.type === 'partnership' ? 'bg-blue-100 text-blue-800' :
                    selectedMessage.type === 'competition' ? 'bg-green-100 text-green-800' :
                    selectedMessage.type === 'media' ? 'bg-purple-100 text-purple-800' :
                    selectedMessage.type === 'athlete' ? 'bg-orange-100 text-orange-800' :
                    'bg-teal-100 text-teal-800'
                  }`}>
                    {selectedMessage.type === 'general' ? 'Général' :
                     selectedMessage.type === 'partnership' ? 'Partenariat' :
                     selectedMessage.type === 'competition' ? 'Compétition' :
                     selectedMessage.type === 'media' ? 'Média' :
                     selectedMessage.type === 'athlete' ? 'Athlète' :
                     'Fédération'}
                  </span>
                  
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    selectedMessage.status === 'new' ? 'bg-blue-100 text-blue-800' :
                    selectedMessage.status === 'read' ? 'bg-gray-100 text-gray-800' :
                    selectedMessage.status === 'replied' ? 'bg-green-100 text-green-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {selectedMessage.status === 'new' ? 'Nouveau' :
                     selectedMessage.status === 'read' ? 'Lu' :
                     selectedMessage.status === 'replied' ? 'Répondu' :
                     'Archivé'}
                  </span>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-gray-200 whitespace-pre-line">
                  {selectedMessage.message}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <a 
                    href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    <span>Répondre</span>
                  </a>
                  
                  <button 
                    onClick={() => handleUpdateStatus(selectedMessage.id, 'replied')}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Check className="h-4 w-4" />
                    <span>Marquer comme répondu</span>
                  </button>
                  
                  <button 
                    onClick={() => handleUpdateStatus(selectedMessage.id, 'archived')}
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Archive className="h-4 w-4" />
                    <span>Archiver</span>
                  </button>
                </div>
                
                <button 
                  onClick={() => {
                    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
                      handleDelete(selectedMessage);
                    }
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Supprimer</span>
                </button>
              </div>
            </div>
          )}
        </AdminModal>
      </div>
    </div>
  );
};

export default AdminMessages;